import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // NZ ROOT FIX: Size variants in this store use the same product media.
    // Replacing an identical gallery creates an unnecessary visual flash and
    // forces the custom gallery layer to rebuild. Keep the existing gallery
    // when the ordered native media IDs and presentation are unchanged.
    const currentSignature = this.#getMediaSignature(this);
    const nextSignature = this.#getMediaSignature(newMediaGallery);
    const samePresentation = this.presentation === newMediaGallery.dataset.presentation;

    if (currentSignature && currentSignature === nextSignature && samePresentation) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Returns the ordered media IDs from the native grid. Custom duplicate
   * thumbnails are excluded, so they don't cause a false difference.
   * @param {Element} gallery
   * @returns {string}
   */
  #getMediaSignature(gallery) {
    const grid = gallery.querySelector('.media-gallery__grid');
    if (!grid) return '';

    return Array.from(grid.children)
      .filter((item) => item instanceof HTMLElement && !item.hasAttribute('data-nz-original-thumb'))
      .map((item) => item.querySelector('.product-media')?.getAttribute('data-media-id') || '')
      .filter(Boolean)
      .join(',');
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
